'use client';

/**
 * ToastProvider Component
 *
 * Global context provider for managing toast state across the application.
 * Handles toast queue, positioning, and auto-dismiss timers.
 */
import React, { createContext, use, useCallback, useState } from 'react';

import { Toast, type ToastProps } from './Toast';

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface ToastItem extends ToastProps {
  id: string;
  position?: ToastPosition;
}

export interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export interface ToastProviderProps {
  children: React.ReactNode;
  /**
   * Maximum number of toasts to show at once
   */
  maxToasts?: number;
  /**
   * Default position for toasts
   */
  defaultPosition?: ToastPosition;
}

const getPositionClasses = (position: ToastPosition): string => {
  const baseClasses = 'fixed z-[100] flex flex-col gap-2 max-w-sm pointer-events-none';

  // Top-anchored toasts get a larger top offset on desktop (md+) so they sit
  // below the sticky app header instead of covering it. Mobile keeps the
  // tighter `top-4` since the mobile header is shorter and the collision is
  // less of an issue there.
  switch (position) {
    case 'top-left':
      return `${baseClasses} top-4 md:top-20 left-4`;
    case 'top-center':
      return `${baseClasses} top-4 md:top-20 left-1/2 transform -translate-x-1/2`;
    case 'top-right':
      return `${baseClasses} top-4 md:top-20 right-4`;
    case 'bottom-left':
      return `${baseClasses} bottom-4 left-4`;
    case 'bottom-center':
      return `${baseClasses} bottom-20 left-4 right-4 max-w-none`;
    case 'bottom-right':
      return `${baseClasses} bottom-4 right-4`;
    default:
      return `${baseClasses} top-4 md:top-20 right-4`;
  }
};

export function ToastProvider({ children, maxToasts = 5, defaultPosition = 'top-right' }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback(
    (toast: Omit<ToastItem, 'id'>): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      const newToast: ToastItem = {
        ...toast,
        id,
        position: toast.position || defaultPosition
      };

      setToasts((prev) => {
        const updated = [newToast, ...prev];
        // Limit number of toasts
        return updated.slice(0, maxToasts);
      });

      return id;
    },
    [maxToasts, defaultPosition]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Group toasts by position
  const toastsByPosition = toasts.reduce(
    (acc, toast) => {
      const position = toast.position || defaultPosition;
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(toast);
      return acc;
    },
    {} as Record<ToastPosition, ToastItem[]>
  );

  const contextValue: ToastContextValue = React.useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      clearAllToasts
    }),
    [toasts, addToast, removeToast, clearAllToasts]
  );

  return (
    <ToastContext value={contextValue}>
      {children}

      {/* Render toasts by position */}
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <div key={position} className={getPositionClasses(position as ToastPosition)}>
          {positionToasts.map((toast) => {
            // Filter out undefined values to prevent React errors
            const cleanToast = {
              id: toast.id,
              variant: toast.variant || 'info',
              title: toast.title,
              description: toast.description,
              duration: toast.duration ?? 5000,
              closable: toast.closable ?? true,
              icon: toast.icon
            };

            return (
              <Toast
                key={toast.id}
                {...cleanToast}
                onDismiss={() => removeToast(toast.id)}
                className="pointer-events-auto transform transition-all duration-300 ease-in-out"
              />
            );
          })}
        </div>
      ))}
    </ToastContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook exported alongside provider
export function useToastContext(): ToastContextValue {
  const context = use(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
}
